import { Link } from 'react-router-dom';
import { PiArrowRightBold } from 'react-icons/pi';
import { LoadingState } from '@v2/design-system';
import { useI18n } from '@v2/providers/I18nProvider';
import type { SellerHistoryMonthItem } from '@v2/pages/commissioning/types';
import { fmtBrl } from './homeFormat';

type Props = {
    records: SellerHistoryMonthItem[];
    historyMonthTo: string;
    isLoading?: boolean;
};

function saleLabel(r: SellerHistoryMonthItem) {
    return (
        r.display_sale_number
        || r.internal_number
        || r.deal_number
        || `#${r.deal_id}`
    );
}

function installmentLabel(r: SellerHistoryMonthItem) {
    if (r.installment_number && r.total_installments) {
        return `${r.installment_number}/${r.total_installments}`;
    }

    return r.status_label || r.competence_at || '—';
}

export default function SellerGeneratedCommissions({ records, historyMonthTo, isLoading = false }: Props) {
    const { t } = useI18n();
    const rows = records.slice(0, 8);

    return (
        <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-900">
            <div className="border-b border-gray-100 px-4 py-3 dark:border-zinc-800">
                <h2 className="text-sm font-bold uppercase tracking-wide text-gray-800 dark:text-zinc-100">
                    {t('v2.my_commission.generated_title')}
                </h2>
            </div>

            {isLoading ? (
                <div className="px-4 py-6">
                    <LoadingState variant="inline" />
                </div>
            ) : rows.length === 0 ? (
                <p className="px-4 py-8 text-center text-sm text-gray-400">
                    {t('v2.my_commission.generated_empty')}
                </p>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[720px] text-sm">
                        <thead className="border-b border-gray-100 text-left text-[10px] uppercase tracking-wide text-gray-500 dark:border-zinc-800">
                            <tr>
                                <th className="px-4 py-2 font-semibold">{t('v2.my_commission.generated_col_sale')}</th>
                                <th className="px-4 py-2 font-semibold">{t('v2.my_commission.generated_col_client')}</th>
                                <th className="px-4 py-2 font-semibold text-right">{t('v2.my_commission.generated_col_deal')}</th>
                                <th className="px-4 py-2 font-semibold text-right">{t('v2.my_commission.generated_col_commission')}</th>
                                <th className="px-4 py-2 font-semibold">{t('v2.my_commission.generated_col_payment')}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50 dark:divide-zinc-800">
                            {rows.map((r) => (
                                <tr key={r.record_id} className="hover:bg-gray-50/80 dark:hover:bg-zinc-800/40">
                                    <td className="px-4 py-3">
                                        <Link
                                            to={`/ploomes-deals/${r.deal_id}?tab=commission`}
                                            className="font-semibold text-orange-700 hover:underline dark:text-orange-400"
                                        >
                                            {saleLabel(r)}
                                        </Link>
                                    </td>
                                    <td className="px-4 py-3">
                                        <p className="font-medium text-gray-900 dark:text-white">
                                            {r.deal_title || '—'}
                                        </p>
                                    </td>
                                    <td className="px-4 py-3 text-right tabular-nums text-gray-700 dark:text-zinc-200">
                                        {r.deal_amount != null ? fmtBrl(r.deal_amount) : '—'}
                                    </td>
                                    <td className="px-4 py-3 text-right font-bold tabular-nums text-orange-700 dark:text-orange-300">
                                        {fmtBrl(r.final_amount)}
                                    </td>
                                    <td className="px-4 py-3 text-xs text-gray-600 dark:text-zinc-300">
                                        {installmentLabel(r)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <div className="border-t border-gray-100 px-4 py-3 dark:border-zinc-800">
                <Link
                    to={historyMonthTo}
                    className="inline-flex items-center gap-1 text-sm font-medium text-orange-700 hover:underline dark:text-orange-400"
                >
                    {t('v2.my_commission.generated_all_link')}
                    <PiArrowRightBold className="size-3.5" />
                </Link>
            </div>
        </section>
    );
}
